import { Button, Icon, Switch, Textarea } from "@/shared/components";
import { cn } from "@/shared/utils";
import { useRef, useState } from "react";
import { applyMarkdownAction, MarkdownAction, markdownActions } from "./markdown-actions.ts";
import { Markdown } from "./markdown.tsx";

type MarkdownEditorProps = {
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    rows?: number;
    className?: string;
    "data-testid"?: string;
};

export function MarkdownEditor({ value, onChange, onBlur, rows = 14, className, ...props }: Readonly<MarkdownEditorProps>) {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [toontVoorbeeld, setToontVoorbeeld] = useState(false);

    const handleAction = (action: MarkdownAction) => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const { waarde, selectie } = applyMarkdownAction(action, value, textarea.selectionStart, textarea.selectionEnd);
        onChange(waarde);

        // Zet de cursor pas terug zodra React de nieuwe waarde gerenderd heeft.
        requestAnimationFrame(() => {
            textarea.focus();
            textarea.setSelectionRange(...selectie);
        });
    };

    return (
        <div className={cn("flex flex-col gap-2", className)}>
            <div className="flex flex-wrap items-center gap-1 rounded-s p-1" role="toolbar" aria-label="Opmaak">
                {markdownActions.map((action) => (
                    <Button
                        key={action.title}
                        type="button"
                        variant="secondary"
                        iconSize="xs"
                        size="s"
                        title={action.title}
                        aria-label={action.title}
                        onClick={() => handleAction(action)}
                        data-testid={`markdown-actie-${action.title.toLowerCase()}`}
                    >
                        {action.icon ? <Icon name={action.icon} size="xs" /> : action.label}
                    </Button>
                ))}
            </div>

            <Textarea
                ref={textareaRef}
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onBlur={onBlur}
                rows={rows}
                className="font-mono"
                placeholder="Schrijf het bericht in markdown"
                data-testid={props["data-testid"] ?? "markdown-editor"}
            />

            <div className="w-auto">
                <Switch
                    checked={toontVoorbeeld}
                    onCheckedChange={setToontVoorbeeld}
                    label="Voorbeeld"
                    data-testid="markdown-voorbeeld-toggle"
                />
            </div>

            {toontVoorbeeld && (
                <div className="border-default bg-elevated-1 rounded-md border p-3" data-testid="markdown-preview">
                    {value.trim() ? (
                        <Markdown markdown={value} />
                    ) : (
                        <p className="text-s text-content-subtle">Het voorbeeld verschijnt zodra je tekst typt.</p>
                    )}
                </div>
            )}
        </div>
    );
}
